// Locale negotiation for requests and documents. Order per ADR 0010: `?locale=`, then
// `Accept-Language`, then the caller's own default (`driver.language`, an organisation setting),
// then the source language.

import { type CatalogLocale, LOCALES, type Locale, SOURCE_LOCALE } from './namespaces'

export interface ResolveLocaleInput<L extends CatalogLocale = Locale> {
  /** Raw value of the `locale` query parameter, if the request carried one. */
  readonly query?: string | null
  /** Raw `Accept-Language` request header. */
  readonly acceptLanguage?: string | null
  /** Used when neither input matches; defaults to the source language. */
  readonly fallback?: L
  /** Defaults to the three shared locales. Pass `localesFor('pdf')` to allow French as well. */
  readonly supported?: readonly L[]
}

interface AcceptLanguageEntry {
  readonly tag: string
  readonly quality: number
}

/**
 * Picks the best supported locale for a request. Region subtags are matched on their language part,
 * so `de-DE` resolves to `de`.
 */
export function resolveLocale<L extends CatalogLocale = Locale>(
  input: ResolveLocaleInput<L> = {},
): L {
  const supported: readonly CatalogLocale[] = input.supported ?? LOCALES
  const fallback: CatalogLocale = input.fallback ?? SOURCE_LOCALE

  const chosen =
    matchTag(input.query, supported) ??
    matchAcceptLanguage(input.acceptLanguage, supported) ??
    (supported.includes(fallback) ? fallback : supported[0]) ??
    SOURCE_LOCALE

  // Safe: every branch above comes from `supported`, whose element type is `L`.
  return chosen as L
}

function matchTag(
  value: string | null | undefined,
  supported: readonly CatalogLocale[],
): CatalogLocale | undefined {
  if (!value) return undefined
  const language = value.trim().toLowerCase().split('-')[0]
  return supported.find((locale) => locale === language)
}

function matchAcceptLanguage(
  header: string | null | undefined,
  supported: readonly CatalogLocale[],
): CatalogLocale | undefined {
  for (const entry of parseAcceptLanguage(header)) {
    const match = matchTag(entry.tag, supported)
    if (match) return match
  }
  return undefined
}

/** Entries with `q=0` are dropped and `*` is ignored; the rest come back in descending quality. */
function parseAcceptLanguage(header: string | null | undefined): readonly AcceptLanguageEntry[] {
  if (!header) return []

  const entries: AcceptLanguageEntry[] = []

  for (const part of header.split(',')) {
    const [rawTag, ...parameters] = part.split(';')
    const tag = rawTag?.trim() ?? ''
    if (tag === '' || tag === '*') continue

    const qualityParameter = parameters.find((parameter) => parameter.trim().startsWith('q='))
    const quality = qualityParameter === undefined ? 1 : Number(qualityParameter.trim().slice(2))
    if (!Number.isFinite(quality) || quality <= 0) continue

    entries.push({ tag, quality })
  }

  // Array#sort is stable, so equal qualities keep the order the client sent them in.
  return entries.sort((a, b) => b.quality - a.quality)
}
